import { CustomAlert } from 'components/AlertsCollection';
import { handleApiError } from 'helper/error';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ResponseError } from 'services/api/base';
import {
  NotifyCompanyChangesParams,
  SaveCompanyParams,
  useGetCompanyByCnpjMutation,
  useNotifyCompanyChangesMutation,
  useSaveCompanyMutation,
} from 'services/api/companies';
import useSaveContract from 'components/Admin/Contracts/Form/useSaveContract';
import useContractData from 'utils/hooks/useContractData';
import { ListedCompany } from 'helper/companies';
import { Contract } from 'helper/contracts';
import useGetCompanyData from './useGetCompanyData';

function numberFromSelect(value: FormDataEntryValue | null) {
  if (value === null || value === '') {
    return undefined;
  }

  return Number(value);
}

export function extractCompanyFormParameters(formData: FormData): SaveCompanyParams {
  return {
    id: Number(formData.get('id')),
    cnpj: String(formData.get('cnpj')),
    ie: String(formData.get('ie')),
    moniker: String(formData.get('moniker')),
    fantasyName: String(formData.get('fantasyName')),
    corporateName: String(formData.get('corporateName')),
    fiscalEmails: String(formData.get('fiscalEmails')),
    address: String(formData.get('address')),
    state: numberFromSelect(formData.get('uf')),
    city: numberFromSelect(formData.get('city')),
    number: String(formData.get('number')),
    complement: String(formData.get('complement')),
    cep: String(formData.get('cep')),
    district: String(formData.get('district')),
    active: true,
  };
}

// Monta o snapshot do estado anterior (cadastral + contrato + produtos) a partir
// do que o frontend já tem em cache, para o backend computar o diff do e-mail.
export function buildBeforeSnapshot(
  companyData?: ListedCompany,
  contractData?: Contract,
): NotifyCompanyChangesParams['before'] {
  return {
    company: companyData && {
      razao_social: companyData.razao_social,
      nome_fantasia: companyData.nome_fantasia,
      apelido: companyData.apelido,
      cnpj: companyData.cnpj,
      ie: companyData.ie,
      im: companyData.im,
      endereco: companyData.endereco,
      numero: companyData.numero,
      complemento: companyData.complemento,
      bairro: companyData.bairro,
      cep: companyData.cep,
      emails_fiscais: companyData.emails_fiscais,
    },
    contract: contractData && {
      id_status: contractData.statusId,
      id_parte: contractData.type,
      dt_inicio: contractData.termStartDate,
      dt_fim: contractData.termEndDate,
    },
    products: contractData?.products?.map((product) => ({
      id_produto: product.productId,
      dt_inicio: product.startDate,
      dt_fim: product.endDate,
      ck_ativo: product.enabled,
    })),
  };
}

export default function useSaveCompany({ companyId, contractId }: {
  companyId: number,
  contractId: number,
}): {
    isLoading: boolean,
    validated: boolean,
    alerts: CustomAlert[],
    requestSuccess: boolean,
    companySaveHook: (event: React.FormEvent<HTMLFormElement>) => Promise<void>
  } {
  const navigate = useNavigate();
  const [saveCompany, { isLoading }] = useSaveCompanyMutation();
  const [getCompanyByCnpj] = useGetCompanyByCnpjMutation();
  const [notifyCompanyChanges] = useNotifyCompanyChangesMutation();
  const { companyData } = useGetCompanyData(companyId);
  const { contractData } = useContractData(contractId);
  const {
    isLoading: isContractLoading,
    alerts: contractAlerts,
    requestSuccess: contractRequestSuccess,
    contractSaveHook,
  } = useSaveContract();
  const [alerts, setAlerts] = useState<CustomAlert[]>([]);
  const [validated, setValidated] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState<boolean>(false);
  const combinedAlerts = useMemo(() => [...alerts, ...contractAlerts], [alerts, contractAlerts]);

  return {
    isLoading: isLoading || isContractLoading,
    alerts: combinedAlerts,
    validated,
    requestSuccess: requestSuccess && contractRequestSuccess,
    companySaveHook: async (event) => {
      // Prevent the browser from reloading the page
      event.preventDefault();

      const form = event.target as HTMLFormElement;
      const formData = new FormData(form);

      if (!form.checkValidity()) {
        setValidated(true);
        return;
      }

      // Captura o estado ANTERIOR (cache do RTK Query) antes de salvar — o e-mail
      // de alterações é montado pelo backend comparando este "antes" com o BD.
      const isEditing = companyId !== 0;
      const beforeSnapshot = buildBeforeSnapshot(companyData, contractData);

      const companyParams: SaveCompanyParams = extractCompanyFormParameters(formData);
      const companySaveResult = await saveCompany(companyParams);

      if ('data' in companySaveResult) {
        const { title, message } = companySaveResult.data;
        const baseAlerts = message instanceof Object
          ? Object.entries(message).map(([t, m]) => ({ title: t, message: m }))
          : [{ title, message }];

        if (companySaveResult.data.status) {
          const successAlerts = baseAlerts.map((baseAlert) => ({ ...baseAlert, variant: 'success' }));
          const companyByCnpj = await getCompanyByCnpj({ companyCnpj: companyParams.cnpj });

          if ('data' in companyByCnpj) {
            const savedCompanyId = companyByCnpj.data.id;
            const contractSaved = await contractSaveHook(event, { companyId: savedCompanyId });

            // Notifica as alterações por e-mail apenas em EDIÇÃO e após o contrato
            // ter salvo. É best-effort: uma falha aqui não invalida o "salvo com
            // sucesso", pois os dados já foram persistidos.
            if (isEditing && contractSaved) {
              await notifyCompanyChanges({
                companyId: savedCompanyId,
                contractId,
                before: beforeSnapshot,
              });
            }
          } else {
            handleApiError(navigate, { status: 500, data: 'something wrong' });
          }
          setRequestSuccess(true);
          setAlerts(successAlerts);
        } else {
          const errorAlerts = baseAlerts.map((baseAlert) => ({ ...baseAlert, variant: 'danger' }));
          setAlerts(errorAlerts);
        }
      } else if ('error' in companySaveResult) {
        handleApiError(navigate, companySaveResult.error as ResponseError);
      }
    },
  };
}
